import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';

import { fetcher, failedJobApi } from '../../lib/api';
import styles from './FailedJobs.module.scss';

const PAGE_SIZE = 30;

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const FailedJobs = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const listUrl = failedJobApi.list({
        page,
        limit: PAGE_SIZE,
        ...(search ? { search } : {}),
    });

    const { data, error, isLoading } = useSWR(listUrl, fetcher, {
        refreshInterval: 10000,
    });

    useEffect(() => {
        if (!data?.results) return;
        setRows((prev) => {
            if (page === 1) return data.results;
            const byId = new Map(prev.map((r) => [r.id, r]));
            data.results.forEach((r) => byId.set(r.id, r));
            return Array.from(byId.values());
        });
    }, [data, page]);

    const totalCount = data?.count ?? rows.length;
    const hasMore = rows.length > 0 && rows.length < totalCount;

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.failed_jobs')} />
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardBody>
                        <div className={styles.listHeader}>
                            <div className={styles.listHeaderCount}>
                                {totalCount} {t('menu.failed_jobs').toLowerCase()}
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <input
                                className={styles.searchInput}
                                value={searchInput}
                                placeholder={t('failed_jobs.search_placeholder')}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearch(searchInput.trim());
                                        setPage(1);
                                        setRows([]);
                                    }
                                }}
                            />
                            <SparaButton
                                color="primary"
                                onClick={() => {
                                    setSearch(searchInput.trim());
                                    setPage(1);
                                    setRows([]);
                                }}
                            >
                                {t('failed_jobs.search')}
                            </SparaButton>
                        </div>

                        {error && (
                            <div className={styles.empty}>
                                {error?.response?.data?.message || t('common.error')}
                            </div>
                        )}

                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead className={styles.tableHead}>
                                    <TableRow>
                                        <TableCell>{t('failed_jobs.col_job')}</TableCell>
                                        <TableCell>{t('failed_jobs.col_queue')}</TableCell>
                                        <TableCell>{t('failed_jobs.col_error')}</TableCell>
                                        <TableCell>{t('failed_jobs.col_failed_at')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={styles.tableBody}>
                                    {isLoading && page === 1 && rows.length === 0 &&
                                        Array.from({ length: 6 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 4 }).map((__, j) => (
                                                    <TableCell key={j}>
                                                        <Skeleton variant="text" width={80} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    {!isLoading && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <div className={styles.empty}>
                                                    {t('failed_jobs.empty')}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <div>{row.job}</div>
                                                    <div className={styles.mono} title={row.uuid}>
                                                        {row.uuid}
                                                    </div>
                                                    {row.batch_id && (
                                                        <div className={styles.progressLabel}>
                                                            batch: {row.batch_id}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>{row.queue}</TableCell>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtnPrimary}
                                                        onClick={() =>
                                                            setExpandedId(
                                                                expandedId === row.id
                                                                    ? null
                                                                    : row.id
                                                            )
                                                        }
                                                    >
                                                        {expandedId === row.id
                                                            ? t('failed_jobs.hide_error')
                                                            : t('failed_jobs.show_error')}
                                                    </button>
                                                    <div className={styles.progressLabel}>
                                                        {(row.exception_summary || '').slice(0, 120)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>{formatDate(row.failed_at)}</TableCell>
                                            </TableRow>
                                            {expandedId === row.id && (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <div className={styles.exceptionBox}>
                                                            {row.exception}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {hasMore && (
                            <div className={styles.loadMore}>
                                <SparaButton color="primary" onClick={() => setPage((p) => p + 1)}>
                                    {t('failed_jobs.load_more')}
                                </SparaButton>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default FailedJobs;
